import { GCInstant } from 'src/lib/gcinstant';
import StateObserver, { user } from 'src/StateObserver';
import { getWebPurchaseDTO } from 'src/lib/iap/iap.util';
import { captureGenericError } from 'src/lib/sentry';
import { ProductId } from 'src/replicant/iap';
import { AtoneServiceType } from 'src/replicant/iap/iap.ruleset';
import { getMutex, synchronized } from 'src/replicant/utils/mutex';
import { stripNonJapaneseFriendly } from 'src/replicant/utils/string';
import {
  AtonePaymentMethod,
  AtonePurchase,
  PurchaseCompleteResult,
} from '../iap.types';
import {
  getRevenueBasePrice,
  getRevenueGrossAnalytics,
} from '../analytics/iap.gross.revenue';

type PaymentOpts = {
  type: AtoneServiceType;
  productId: ProductId;
  price: number;
  amount: number;
  description: string;
};

export type AtonePaymentItem = {
  shop_item_id: string;
  item_name: string;
  item_price: number;
  item_count: number;
};

export type AtonePayment = {
  amount: number;
  shop_transaction_no: string; // Must be unique within the store
  // You may create and settle the transaction simultaneously by setting the order settlement (`sales_settled`) to `true`.
  // If you set `sales_settled` as `false`, the transaction will not be settled after it has been created
  sales_settled: boolean;
  description: string;
  // transaction_options: string[];
  user_no: string;
  customer: {
    customer_name: string;
    shop_customer_id: string;
    // Maybe optional
    phone_number?: string;
    address?: string;
    email?: string;
  };
  items: AtonePaymentItem[];
  checksum?: string;
};

type AtoneService = {
  service_type: AtoneServiceType;
  /** Authentication token. */
  pre_token?: string;
  pub_key: string;
  payment: AtonePayment;
};

/**
 * Result of successful Atone payment
 */
export type AtonePaymentSuccess = {
  service_type: string;
  authorization_result: string;
  company_code: string;
  shop_branch_code: string;
  transaction_id: string;
  shop_transaction_no: string;
};

/**
 * Custom error that we return on failed Atone payment
 */
export class AtonePaymentError extends Error {
  constructor(
    message: string,
    public code: 'CANCELED' | 'SERVICE_ERROR' | 'CLIENT_ERROR',
    public payment: AtonePayment,
  ) {
    super(message);
    this.name = 'AtonePaymentError';
  }
}
export type AtoneApi = {
  merge: (data: { services?: Partial<AtoneService>[] }) => void;
  start: (service: AtoneServiceType) => void;
  config: (opts: {
    services: AtoneService[];
    /** Called when authentication is complete. */
    // Authentication token does not expire but can go invalid if user logs out of Atone, changes password or cancels their account;
    // this needs to be handled in the error callback.
    authenticated: (response: {
      service_type: AtoneServiceType;
      authentication_token: string;
    }) => void;

    /** Called when modal is closed. */
    cancelled: () => void;

    /** Called when the form is closed by pressing the button, after payment is rejected. */
    failed: (response: unknown) => void;

    /** Called when payment is accepted and form is automatically closed. */
    succeeded: (response: AtonePaymentSuccess) => void;

    /** Called when Javascript error, an unexpected server error, or merchant’s parameter setting error occurs. */
    error: (name: string, message: string, errors: unknown[]) => void;
  }) => void;
};

declare global {
  interface Window {
    Np: AtoneApi;
  }
}

let sdkCache: Promise<AtoneApi> | undefined = undefined;

/**
 *  Load atone SDK
 * https://manual.atone.be/en/connection_method/
 */
export async function connectAtoneSDK(): Promise<AtoneApi> {
  const prod = 'https://im.np-payment-gateway.com/module/np.js';
  const dev = 'https://ctim.np-payment-gateway.com/module/np.js';

  if (sdkCache == null) {
    sdkCache = new Promise((resolve, reject) => {
      const scriptElement = document.createElement('script');
      scriptElement.onload = () => {
        resolve(window.Np);
      };
      scriptElement.onerror = reject;
      scriptElement.src = process.env.IS_DEVELOPMENT ? dev : prod;
      document.body.appendChild(scriptElement);
    });
  }

  return sdkCache;
}

const atoneTokenRepository = (() => {
  const tokenKey = 'ATONE_AUTH_TOKEN';
  return {
    get: () => localStorage.getItem(tokenKey) ?? undefined,
    put: (token: string | undefined) =>
      token === undefined
        ? localStorage.removeItem(tokenKey)
        : localStorage.setItem(tokenKey, token),
  };
})();

const atoneTrxMutex = getMutex();

/**
 * Start Atone payment process
 *
 * @returns {Promise<AtonePaymentSuccess>}
 * @throws {AtonePaymentError}
 */
export async function atoneInitPayment(
  opts: PaymentOpts,
): Promise<AtonePaymentSuccess> {
  if (sdkCache === undefined) {
    throw new Error('Atone SDK not loaded');
  }

  const sdk = await sdkCache;
  const { type: serviceType, productId, price, amount, description } = opts;

  const payment = {
    amount: price,
    sales_settled: true, // You may create and settle the transaction simultaneously by setting the order settlement (`sales_settled`) to `true`. If you set `sales_settled` as `false`, the transaction will not be settled after it has been created
    description,
    user_no: user().id,
    customer: {
      customer_name: stripNonJapaneseFriendly(GCInstant.playerName),
      shop_customer_id: user().id,
    },
    items: [
      {
        shop_item_id: productId,
        item_name: description,
        item_price: price,
        item_count: amount,
      },
    ],
  } as AtonePayment;

  payment.checksum =
    await StateObserver.replicant.asyncGetters.generateAtonePaymentChecksum(
      payment,
    );

  // Create unique transaction number
  payment.shop_transaction_no = `${user().id}-${opts.productId}-${Date.now()}`; // Must be unique within the store

  return await synchronized(atoneTrxMutex, () => {
    const authToken = atoneTokenRepository.get();
    const transaction = new Promise<AtonePaymentSuccess>((resolve, reject) => {
      sdk.config({
        services: [
          {
            service_type: AtoneServiceType.ATONE, // 01
            pub_key: process.env.ATONE_PUBLISHABLE_KEY!, // read from https://ct-shop.a-to-ne.jp/co_SVHyDcWDuyZv9Toe/settings/shops/sh_4bTLhCKW0FKVKiJV
            pre_token: authToken,
            payment,
          },
          {
            service_type: AtoneServiceType.ATONE_LITE, // 04
            pub_key: process.env.ATONE_PUBLISHABLE_KEY!, // read from https://ct-shop.a-to-ne.jp/co_SVHyDcWDuyZv9Toe/settings/shops/sh_4bTLhCKW0FKVKiJV
            pre_token: authToken,
            payment,
          },
        ],

        authenticated: (response) => {
          if (response.service_type === AtoneServiceType.ATONE) {
            sdk.merge({
              services: [
                {
                  service_type: AtoneServiceType.ATONE,
                  pre_token: response.authentication_token,
                },
              ],
            });

            // Save auth token to make next purchases without login
            atoneTokenRepository.put(response.authentication_token);
          }
        },

        // Purchase completed check on server and give reward
        succeeded: (trx) => resolve(trx),

        // Ignore, user cancelled
        cancelled: () => {
          // Update transaction number for subsequent payment attempts
          updateAtoneTransaction(sdk, payment, { serviceType });

          reject(
            new AtonePaymentError(
              'User cancelled payment',
              'CANCELED',
              payment,
            ),
          );
        },

        // Do nothing, Atone and user issues
        failed: () => {
          // Update transaction number for subsequent payment attempts
          updateAtoneTransaction(sdk, payment, { serviceType });

          reject(
            new AtonePaymentError('Payment failed', 'SERVICE_ERROR', payment),
          );
        },

        // Error on our side
        error: async (...args) => {
          captureGenericError('Failed to complete Atone payment', args, {
            extra: { payment },
          });

          // In any unclear situation we clear auth token
          atoneTokenRepository.put(undefined);

          // Update transaction for subsequent payment attempts
          updateAtoneTransaction(sdk, payment, { serviceType });

          reject(
            new AtonePaymentError('Payment failed', 'CLIENT_ERROR', payment),
          );
        },
      });
    });

    // Ensure current payment details are used
    updateAtoneTransaction(sdk, payment, { serviceType });

    // Start payment flow
    sdk.start(serviceType);

    return transaction;
  });
}

function updateAtoneTransaction(
  sdk: AtoneApi,
  payment: AtonePayment,
  args: {
    serviceType: AtoneServiceType;
  },
) {
  sdk.merge({
    services: [
      {
        service_type: args.serviceType,
        payment,
      },
    ],
  });
}

/**
 * Atone finish purchase flow
 */
export async function atoneCompletePurchase(
  productId: ProductId,
  transactionId: string,
  transactionType: AtoneServiceType,
): Promise<PurchaseCompleteResult> {
  try {
    // Complete payment intent
    await StateObserver.invoke.asyncCompleteAtonePayment({
      productId,
      transactionId,
      transactionType,
    });

    // 01: Atone BNPL(Pay Later), 04: Atone Lite
    const paymentMethod: AtonePaymentMethod =
      transactionType === '01' ? 'atone_bnpl' : 'atone_lite';

    const purchase: AtonePurchase = {
      provider: 'atone',
      providerMethod: paymentMethod,
    };

    // Payment completed, get purchase info
    return {
      response: getWebPurchaseDTO(productId),
      revenue: getRevenueGrossAnalytics(productId, {
        ...purchase,
        ...getRevenueBasePrice(productId),
      }),
      purchaseID: transactionId,
    };
  } catch (e) {
    captureGenericError('Failed to complete Atone payment', e);
    throw new Error('Failed to complete Atone payment: ' + e);
  }
}
